import Chart from 'react-apexcharts';
import {
  AuditChartGranularity,
  AuditChartMetric,
  AuditChartPoint,
} from '../models';
import * as St from '../styles';
import { formatNumber } from '../utils';
import SegmentedControl, { SegmentedOption } from './SegmentedControl';

const PRIMARY = '#e4003a';

const METRIC_OPTIONS: { id: AuditChartMetric; label: string }[] = [
  { id: 'VISITAS', label: 'Visitas' },
  { id: 'CHAMADAS', label: 'Chamadas API' },
];

const METRIC_LABEL: Record<AuditChartMetric, string> = {
  VISITAS: 'Visitas às telas',
  CHAMADAS: 'Chamadas de API',
};

interface VisitsChartProps {
  points: AuditChartPoint[];
  metric: AuditChartMetric;
  granularity: AuditChartGranularity;
  /** Período longo demais para agrupar por hora (máximo de 1000 pontos no back). */
  hourlyDisabled?: boolean;
  loading?: boolean;
  errorMessage?: string;
  onMetricChange: (metric: AuditChartMetric) => void;
  onGranularityChange: (granularity: AuditChartGranularity) => void;
}

const VisitsChart: React.FC<VisitsChartProps> = ({
  points,
  metric,
  granularity,
  hourlyDisabled = false,
  loading = false,
  errorMessage,
  onMetricChange,
  onGranularityChange,
}) => {
  const granularityOptions: SegmentedOption<AuditChartGranularity>[] = [
    {
      id: 'HORA',
      label: 'Hora',
      disabled: hourlyDisabled,
      hint: 'Período longo demais para agrupar por hora',
    },
    { id: 'DIA', label: 'Dia' },
  ];

  const peak = points.reduce<AuditChartPoint | null>(
    (max, point) => (!max || point.valor > max.valor ? point : max),
    null,
  );
  const unit = metric === 'VISITAS' ? 'visitas' : 'chamadas';
  // Com valores baixos, menos marcações evitam rótulos repetidos (0, 0, 1, 1).
  const maxValue = peak?.valor ?? 0;
  const yTicks = Math.max(1, Math.min(4, maxValue));

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Open Sans, sans-serif',
    },
    colors: [PRIMARY],
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: { opacityFrom: 0.25, opacityTo: 0, stops: [0, 100] },
    },
    markers: {
      // Muitos pontos (ex.: 30 dias por hora) viram ruído; o marcador só aparece no hover.
      size: points.length > 48 ? 0 : 4,
      colors: ['#fff'],
      strokeColors: PRIMARY,
      strokeWidth: 2,
      hover: { size: 5 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: '#eaecef',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: points.map((point) => point.label),
      tickAmount: 4,
      labels: { rotate: 0, style: { colors: '#6b7280', fontSize: '10px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: maxValue < 4 ? Math.max(1, maxValue) : undefined,
      tickAmount: yTicks,
      forceNiceScale: maxValue >= 4,
      labels: {
        style: { colors: '#6b7280', fontSize: '10px' },
        formatter: (value) => formatNumber(Math.round(value)),
      },
    },
    tooltip: { y: { formatter: (value) => `${formatNumber(value)} ${unit}` } },
    // Sem nenhum registro no período, não há pico para destacar.
    annotations:
      peak && peak.valor > 0
        ? {
            xaxis: [
              {
                x: peak.label,
                strokeDashArray: 3,
                borderColor: '#c1c1c1',
              },
            ],
            points: [
              {
                x: peak.label,
                y: peak.valor,
                marker: { size: 0 },
                label: {
                  text:
                    granularity === 'HORA'
                      ? `Pico: ${formatNumber(
                          peak.valor,
                        )} ${unit} em ${peak.label.replace(' ', ' às ')}`
                      : `Pico: ${formatNumber(peak.valor)} ${unit} em ${
                          peak.label
                        }`,
                  borderColor: '#1f2937',
                  offsetY: -6,
                  style: {
                    background: '#1f2937',
                    color: '#fff',
                    fontSize: '10px',
                    padding: { left: 8, right: 8, top: 3, bottom: 3 },
                  },
                },
              },
            ],
          }
        : {},
  };

  return (
    <St.Card padding={30}>
      <St.CardHeader>
        <St.CardTitle nowrap>
          <h2>Visitas e chamadas ao longo do tempo</h2>
          <span>Compare a navegação com o volume de chamadas da API</span>
        </St.CardTitle>
        <St.Controls>
          <SegmentedControl
            options={METRIC_OPTIONS}
            value={metric}
            onChange={onMetricChange}
          />
          <SegmentedControl
            options={granularityOptions}
            value={granularity}
            onChange={onGranularityChange}
          />
        </St.Controls>
      </St.CardHeader>
      {loading && (
        <St.ChartMessage role="status" aria-live="polite">
          <St.Spinner />
        </St.ChartMessage>
      )}
      {!loading && errorMessage && (
        <St.ChartMessage role="alert">{errorMessage}</St.ChartMessage>
      )}
      {!loading && !errorMessage && (
        <>
          <Chart
            type="area"
            height={340}
            options={options}
            series={[
              { name: METRIC_LABEL[metric], data: points.map((p) => p.valor) },
            ]}
          />
          <St.Legend>{METRIC_LABEL[metric]}</St.Legend>
        </>
      )}
    </St.Card>
  );
};

export default VisitsChart;
